import React, { useEffect, useMemo, useState } from "react";
import {
  createCompany,
  deleteCompany,
  fetchCompanies,
  updateCompany,
} from "../../services/organization";
import "./organization.css";

const initialForm = {
  name: "",
  address: "",
  tax_id: "",
  logo: "",
};

function normalizeList(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

export default function OrganizationProfilePage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const response = await fetchCompanies();
      setItems(normalizeList(response));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load companies.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }

    return items.filter((item) =>
      [item?.name, item?.address, item?.tax_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [items, search]);

  function resetForm() {
    setForm(initialForm);
    setEditingId(null);
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      name: item?.name || "",
      address: item?.address || "",
      tax_id: item?.tax_id || "",
      logo: item?.logo || "",
    });
    setSuccess("");
    setError("");
  }

  async function handleDelete(id) {
    const confirmed = window.confirm("Delete this company record?");
    if (!confirmed) {
      return;
    }

    try {
      await deleteCompany(id);
      setSuccess("Company deleted successfully.");
      if (editingId === id) {
        resetForm();
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete company.");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (editingId) {
        await updateCompany(editingId, form);
        setSuccess("Company updated successfully.");
      } else {
        await createCompany(form);
        setSuccess("Company created successfully.");
      }

      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save company.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="org-page">
      <section className="org-card org-page__header">
        <div>
          <span className="org-page__eyebrow">Organisation Setup</span>
          <h1>Organization Profile</h1>
          <p>Manage institution or company profile records used across payroll setup.</p>
        </div>

        <div className="org-page__toolbar">
          <input
            className="org-page__search"
            type="text"
            placeholder="Search organization records..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {editingId ? (
            <button
              type="button"
              className="org-page__button org-page__button--light"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </section>

      {error ? <div className="org-error">{error}</div> : null}
      {success ? <div className="org-success">{success}</div> : null}

      <section className="org-card org-page__content">
        <form className="org-form" onSubmit={handleSubmit}>
          <div className="org-field">
            <label htmlFor="company_name">Organization Name</label>
            <input
              id="company_name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter organization name"
              required
            />
          </div>

          <div className="org-field">
            <label htmlFor="company_tax_id">Tax ID</label>
            <input
              id="company_tax_id"
              type="text"
              value={form.tax_id}
              onChange={(e) => setForm((prev) => ({ ...prev, tax_id: e.target.value }))}
              placeholder="Enter tax ID"
            />
          </div>

          <div className="org-field">
            <label htmlFor="company_logo">Logo Path / URL</label>
            <input
              id="company_logo"
              type="text"
              value={form.logo}
              onChange={(e) => setForm((prev) => ({ ...prev, logo: e.target.value }))}
              placeholder="Optional logo path"
            />
          </div>

          <div className="org-field">
            <label htmlFor="company_address">Address</label>
            <textarea
              id="company_address"
              value={form.address}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="Enter address"
              rows={4}
              required
            />
          </div>

          <div className="org-form__actions">
            <button className="org-page__button" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Company" : "Create Company"}
            </button>
          </div>
        </form>
      </section>

      <section className="org-card org-page__content">
        {loading ? (
          <div className="org-loading">Loading organization records...</div>
        ) : filteredItems.length === 0 ? (
          <div className="org-empty">No organization records found.</div>
        ) : (
          <div className="org-table-wrap">
            <table className="org-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tax ID</th>
                  <th>Address</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="org-table__title">{item?.name || "-"}</div>
                    </td>
                    <td>{item?.tax_id || "-"}</td>
                    <td>{item?.address || "-"}</td>
                    <td>
                      <div className="org-actions">
                        <button
                          type="button"
                          className="org-action-btn"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="org-action-btn org-action-btn--danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
